import React, { useEffect, useState, useRef, useCallback } from 'react';
import { doc, onSnapshot, collection, addDoc, Timestamp, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import MessageInput from './finChatsInput';
import MessageBubble from './finChatsBubble';
import TypingIndicator from './TypingIndicator';
import { ProfileImage } from '../ui/ProfileImage';
import { User, Users, X, PersonStanding, Move, ArrowUpRight, ExternalLink, Pin, Bookmark, Star, Settings, Edit3, UserPlus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { chatNotificationService } from '../../services/chatNotificationService';
import { readReceiptsService } from '../../services/readReceiptsService';
import { useChatBar } from '../../context/ChatBarContext';
import { useFloatingChat } from '../../context/FloatingChatContext';
import { typingIndicatorService, TypingUser } from '../../services/typingIndicatorService';
import { UserRole } from '../../lib/types/auth';
import { ClaireGuideButton } from '../claire/ClaireGuideButton';
import { isClaireEmail } from '../../lib/claire/guide';

interface ReplyTo {
  messageId: string;
  text: string;
  sender: string;
  senderName?: string;
}

interface Message {
  id: string;
  sender: string;
  text: string;
  imageUrl?: string;
  attachment?: {
    url: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadPath: string;
  };
  timestamp: Timestamp;
  editedAt?: Timestamp;
  isEdited?: boolean;
  isSystemMessage?: boolean;
  replyTo?: ReplyTo;
  readBy?: Array<{
    userId: string;
    readAt: Timestamp;
  }>;
  reactions?: {
    thumbsUp?: string[];
    thumbsDown?: string[];
    heart?: string[];
    laugh?: string[];
    haha?: string[];
    sad?: string[];
    party?: string[];
    angry?: string[];
  };
}

interface ChatThreadProps {
  chatId: string;
  selectedUserId?: string | null;
  isReadOnly?: boolean;
}

const ChatThread: React.FC<ChatThreadProps> = ({ chatId, selectedUserId = null, isReadOnly = false }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { openChat, isChatOpen } = useChatBar();
  const { activateFloatingChat } = useFloatingChat();
  const [chatData, setChatData] = useState<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  const [userPhotoURLs, setUserPhotoURLs] = useState<Map<string, string>>(new Map());
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [visibleMessages, setVisibleMessages] = useState<Set<string>>(new Set());
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [isMasterAdmin, setIsMasterAdmin] = useState(false);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newChatName, setNewChatName] = useState('');
  
  // Add user to chat state
  const [showAddUser, setShowAddUser] = useState(false);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [addUserSearch, setAddUserSearch] = useState('');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  
  // Reply to message state
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);

  // Function to fetch user data (display name and photo URL)
  const fetchUserData = async (email: string): Promise<{ displayName: string; photoURL?: string }> => {
    if (userDisplayNames.has(email)) {
      return {
        displayName: userDisplayNames.get(email)!,
        photoURL: userPhotoURLs.get(email)
      };
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const displayName = userData.displayName || email;
        const photoURL = userData.photoURL;
        
        setUserDisplayNames(prev => new Map(prev).set(email, displayName));
        if (photoURL) {
          setUserPhotoURLs(prev => new Map(prev).set(email, photoURL));
        }
        
        return { displayName, photoURL };
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    
    // Fallback to email
    setUserDisplayNames(prev => new Map(prev).set(email, email));
    return { displayName: email };
  };

  // Helper functions to get user data
  const getDisplayName = (email: string): string => {
    return userDisplayNames.get(email) || email;
  };

  const getPhotoURL = (email: string): string | undefined => {
    return userPhotoURLs.get(email);
  };

  const handleCloseChat = () => {
    navigate('/chats');
  };

  const handleFollowChat = () => {
    if (!chatData || !currentUser?.email) {
      return;
    }
    
    const displayName = chatData.name || 'Chat';
    const cleanChatData = {
      participants: chatData.participants,
      isPrivate: chatData.isPrivate,
      name: chatData.name,
      displayName
    };
    
    activateFloatingChat(chatId, cleanChatData);
    toast.success('Chat is now following you!');
    navigate('/chats');
  };

  const handleRenameChat = async () => {
    if (!newChatName.trim() || !chatData) return;
    
    try {
      const chatRef = doc(db, 'chats', chatId);
      await updateDoc(chatRef, {
        name: newChatName.trim(),
        updatedAt: Timestamp.now()
      });
      
      setIsEditingName(false);
      setNewChatName('');
      toast.success('Chat renamed successfully!');
    } catch (error) {
      console.error('Error renaming chat:', error);
      toast.error('Failed to rename chat');
    }
  };

  const startEditingName = () => {
    setNewChatName(chatData?.name || '');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setNewChatName('');
  };

  // Load all users for add user functionality
  const loadAllUsers = async () => {
    if (allUsers.length > 0) return; // Already loaded
    
    setIsLoadingUsers(true);
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      
      const users = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((user: any) => user.email !== currentUser?.email) // Exclude current user
        .sort((a: any, b: any) => (a.displayName || a.email).localeCompare(b.displayName || b.email));
      
      setAllUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Filter users based on search
  const filterUsers = (searchTerm: string) => {
    setAddUserSearch(searchTerm);
    if (!searchTerm.trim()) {
      setFilteredUsers(allUsers);
      return;
    }
    
    const filtered = allUsers.filter((user: any) => 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  // Add user to existing chat and send system message
  const addUserToExistingChat = async (userToAdd: any) => {
    if (!chatData || !currentUser?.email || isAddingUser) return;
    
    const userEmail = userToAdd.email.toLowerCase();
    
    // Check if user is already in the chat
    if (chatData.participants?.includes(userEmail)) {
      toast.error('User is already in this chat');
      return;
    }
    
    setIsAddingUser(true);
    try {
      const chatRef = doc(db, 'chats', chatId);
      const updatedParticipants = [...(chatData.participants || []), userEmail];
      
      // If this was a private chat, convert it to a group chat
      const isConvertingToGroup = chatData.isPrivate;
      
      // Update the chat document
      await updateDoc(chatRef, {
        participants: updatedParticipants,
        isPrivate: false, // Now it's a group chat
        updatedAt: Timestamp.now(),
        ...(isConvertingToGroup && !chatData.name && { name: 'Group Chat' })
      });
      
      // Get display names for the system message
      const adderName = getDisplayName(currentUser.email) || currentUser.email;
      const addedName = userToAdd.displayName || userToAdd.email;
      
      // Send system message
      const systemMessage = {
        sender: 'system',
        text: `${adderName} added ${addedName} to the chat`,
        timestamp: Timestamp.now(),
        isSystemMessage: true
      };
      
      await addDoc(collection(db, 'chats', chatId, 'messages'), systemMessage);
      
      // Update last message
      await updateDoc(chatRef, {
        lastMessage: systemMessage.text
      });
      
      toast.success(`Added ${addedName} to the chat!`);
      setShowAddUser(false);
      setAddUserSearch('');
      setFilteredUsers(allUsers);
      
      // Fetch user data for the new participant
      fetchUserData(userEmail);
      
    } catch (error) {
      console.error('Error adding user to chat:', error);
      toast.error('Failed to add user to chat');
    } finally {
      setIsAddingUser(false);
    }
  };

  // Mark messages as read when they become visible
  const markMessagesAsRead = async (messageIds: string[]) => {
    if (!currentUser?.email || isReadOnly || messageIds.length === 0) return;
    
    try {
      // Filter out messages that are from the current user (don't need to mark own messages as read)
      const messagesToMark = messages
        .filter(msg => messageIds.includes(msg.id) && msg.sender !== currentUser.email)
        .map(msg => msg.id);
      
      if (messagesToMark.length > 0) {
        await readReceiptsService.markMessagesAsRead(chatId, messagesToMark, currentUser.email);
        console.log(`✓ Marked ${messagesToMark.length} messages as read`);
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Check if current user is Master Admin
  useEffect(() => {
    const checkMasterAdmin = async () => {
      if (currentUser?.email) {
        try {
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('email', '==', currentUser.email));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            const isMaster = userData.roles?.includes(UserRole.MasterAdmin) || false;
            console.log('🔍 Master Admin Check:', {
              email: currentUser.email,
              roles: userData.roles,
              isMasterAdmin: isMaster
            });
            setIsMasterAdmin(isMaster);
          }
        } catch (error) {
          console.error('Error checking Master Admin status:', error);
        }
      }
    };

    checkMasterAdmin();
  }, [currentUser]);

  useEffect(() => {
    const chatRef = doc(db, 'chats', chatId);
    const unsubChat = onSnapshot(chatRef, (docSnap) => {
      if (docSnap.exists()) {
        setChatData(docSnap.data());
      }
    });

    const messagesRef = collection(db, 'chats', chatId, 'messages');
    const unsubMessages = onSnapshot(messagesRef, async (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message));
      
      // Fetch user data for all message senders
      const senders = new Set(msgs.map(msg => msg.sender));
      const fetchPromises = Array.from(senders)
        .filter(email => !userDisplayNames.has(email))
        .map(email => fetchUserData(email));
      
      await Promise.allSettled(fetchPromises);
      
      const sortedMessages = msgs.sort((a, b) => {
        const aTime = a.timestamp?.seconds || 0;
        const bTime = b.timestamp?.seconds || 0;
        return aTime - bTime;
      });
      
      setMessages(sortedMessages);

      // Mark all messages as read when they're loaded (user is viewing the chat)
      if (currentUser?.email && !isReadOnly && sortedMessages.length > 0) {
        const messageIds = sortedMessages.map(msg => msg.id);
        markMessagesAsRead(messageIds);
        
        // Also mark the chat as read in the notification service
        chatNotificationService.markChatAsRead(currentUser.email, chatId);
      }
    }, (error) => {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    });

    return () => {
      unsubChat();
      unsubMessages();
    };
  }, [chatId, currentUser?.email, isReadOnly]);

  // Set up typing indicator subscription for Master Admin only
  useEffect(() => {
    let unsubTyping: (() => void) | null = null;
    
    console.log('⚡ Setting up typing subscription:', {
      isMasterAdmin,
      currentUserUid: currentUser?.uid,
      currentUserEmail: currentUser?.email,
      chatId
    });
    
    if (isMasterAdmin && currentUser?.uid && currentUser?.email) {
      typingIndicatorService.subscribeToTypingIndicators(
        chatId,
        currentUser.uid,
        currentUser.email,
        (users) => {
          console.log('👥 Typing users updated:', users);
          setTypingUsers(users);
        }
      ).then((unsubscribe) => {
        console.log('✅ Typing subscription established:', !!unsubscribe);
        unsubTyping = unsubscribe;
      });
    } else {
      console.log('❌ Typing subscription not established - missing requirements');
    }

    return () => {
      if (unsubTyping) {
        console.log('🧹 Cleaning up typing subscription');
        unsubTyping();
      }
    };
  }, [chatId, currentUser?.uid, currentUser?.email, isMasterAdmin]);

  // Scroll to bottom when new messages arrive (within container only)
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark messages as read when user opens the chat (only if not in read-only mode)
  useEffect(() => {
    if (currentUser?.email && chatId && !isReadOnly && messages.length > 0) {
      const messageIds = messages.map(msg => msg.id);
      markMessagesAsRead(messageIds);
      
      // Also mark the chat as read in the notification service
      chatNotificationService.markChatAsRead(currentUser.email, chatId);
    }
  }, [chatId, currentUser?.email, isReadOnly, messages]);

  // Mark messages as read when window gains focus (user comes back to the tab)
  useEffect(() => {
    const handleFocus = () => {
      if (currentUser?.email && chatId && !isReadOnly && messages.length > 0) {
        const messageIds = messages.map(msg => msg.id);
        markMessagesAsRead(messageIds);
        
        // Also mark the chat as read in the notification service
        chatNotificationService.markChatAsRead(currentUser.email, chatId);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser?.email && chatId && !isReadOnly && messages.length > 0) {
        const messageIds = messages.map(msg => msg.id);
        markMessagesAsRead(messageIds);
        
        // Also mark the chat as read in the notification service
        chatNotificationService.markChatAsRead(currentUser.email, chatId);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chatId, currentUser?.email, isReadOnly]);

  const sendMessage = async (text: string, imageUrl?: string, attachment?: any) => {
    if (!currentUser?.email || (!text.trim() && !imageUrl && !attachment)) return;

    try {
      const message: any = {
        sender: currentUser.email,
        text: text.trim(),
        ...(imageUrl && { imageUrl }),
        ...(attachment && { attachment }),
        timestamp: Timestamp.now()
      };

      // Add reply data if replying to a message
      if (replyToMessage) {
        message.replyTo = {
          messageId: replyToMessage.id,
          text: replyToMessage.text?.substring(0, 100) || (replyToMessage.imageUrl ? '📷 Image' : '📎 Attachment'),
          sender: replyToMessage.sender,
          senderName: getDisplayName(replyToMessage.sender)
        };
      }

      // Add the message to the messages subcollection
      await addDoc(collection(db, 'chats', chatId, 'messages'), message);

      // Update the parent chat document with the latest message info
      const chatRef = doc(db, 'chats', chatId);
      let lastMessage = text.trim();
      if (imageUrl && !text.trim()) {
        lastMessage = '📷 Image';
      } else if (attachment && !text.trim()) {
        lastMessage = `📎 ${attachment.fileName}`;
      } else if (attachment && text.trim()) {
        lastMessage = `📎 ${attachment.fileName}: ${text.trim()}`;
      }
      
      await updateDoc(chatRef, {
        lastMessage,
        lastMessageSender: message.sender,
        updatedAt: Timestamp.now()
      });

      // Clear the reply state after sending
      setReplyToMessage(null);

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  // Handle reply to message
  const handleReplyToMessage = (message: Message) => {
    setReplyToMessage(message);
  };

  // Cancel reply
  const cancelReply = () => {
    setReplyToMessage(null);
  };

  const editMessage = async (messageId: string, newText: string) => {
    if (!currentUser?.email || !newText.trim()) return;

    try {
      const messageRef = doc(db, 'chats', chatId, 'messages', messageId);
      await updateDoc(messageRef, {
        text: newText.trim(),
        editedAt: Timestamp.now(),
        isEdited: true
      });

      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error('Failed to edit message');
    }
  };

  if (!chatData) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading chat...</div>
      </div>
    );
  }

  const otherParticipants = chatData.participants?.filter((p: string) => p !== currentUser?.email) || [];
  const displayName = chatData.name || otherParticipants.map((email: string) => getDisplayName(email)).join(', ') || 'Chat';

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      <div className="group px-6 py-4 border-b border-gray-200 flex items-center gap-4 bg-gray-50">
        {chatData.isPrivate && otherParticipants.length === 1 ? (
          // Show profile picture for 1-on-1 chats
          <div className="flex-shrink-0">
            <ProfileImage
              src={getPhotoURL(otherParticipants[0])}
              alt={getDisplayName(otherParticipants[0])}
              className="w-10 h-10 rounded-full"
              neutral
            />
          </div>
        ) : (
          // Show icon for group chats
          <div className="w-10 h-10 rounded-full bg-[#EFE3C4] 
                         flex items-center justify-center flex-shrink-0 cursor-pointer hover:bg-[#E2D2AC] transition-colors"
                         onClick={() => setShowGroupSettings(true)}
                         title="View group members">
            <Users className="w-5 h-5 text-[#7A5A34]" />
          </div>
        )}
        <div className="flex-1 cursor-pointer" onClick={!chatData.isPrivate ? () => setShowGroupSettings(true) : undefined}>
          {isEditingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newChatName}
                onChange={(e) => setNewChatName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameChat();
                  if (e.key === 'Escape') cancelEditingName();
                }}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-gray-900 text-sm flex-1 focus:outline-none focus:border-teal-400"
                placeholder="Enter chat name..."
                autoFocus
              />
              <button
                onClick={handleRenameChat}
                className="p-1 hover:bg-gray-100 rounded text-green-600 hover:text-green-700"
                title="Save"
              >
                ✓
              </button>
              <button
                onClick={cancelEditingName}
                className="p-1 hover:bg-gray-100 rounded text-red-600 hover:text-red-700"
                title="Cancel"
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h2 className="font-medium text-gray-900">{displayName}</h2>
                {!chatData.isPrivate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditingName();
                    }}
                    className="p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Rename chat"
                  >
                    <Edit3 className="w-3 h-3 text-gray-500" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {chatData.isPrivate ? '1-on-1 conversation' : `${chatData.participants?.length || 0} participants`}
              </p>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {chatData.participants?.some((p: string) => isClaireEmail(p)) && (
            <ClaireGuideButton className="p-2 rounded-full border border-gray-200 hover:border-[#d9c7a8]" />
          )}
          {/* Add User Button */}
          <button
            onClick={() => {
              setShowGroupSettings(true);
              setShowAddUser(true);
              loadAllUsers();
            }}
            className="p-2 hover:bg-teal-100 rounded-full transition-colors text-gray-500 hover:text-teal-600 border border-gray-200 hover:border-teal-300"
            title="Add user to chat"
          >
            <UserPlus className="w-5 h-5" />
          </button>
          
          {/* Open in Chat Bar Button */}
          <button
            onClick={() => {
              const chatParticipants = chatData.participants?.filter((e: string) => e !== currentUser?.email) || [];
              const displayName = chatData.name || chatParticipants.map(email => getDisplayName(email)).join(', ') || 'Chat';
              openChat(chatId, {
                displayName,
                name: displayName,
                photoURL: chatData.isPrivate && chatParticipants.length === 1 
                  ? getPhotoURL(chatParticipants[0]) 
                  : null,
                isGroup: !chatData.isPrivate || chatParticipants.length > 1
              });
              toast.success('Chat opened in chat bar!');
            }}
            className={`p-2 hover:bg-teal-100 rounded-full transition-colors border border-gray-200 hover:border-teal-300 ${
              isChatOpen(chatId) ? 'bg-teal-100 text-teal-600' : 'text-gray-500 hover:text-teal-600'
            }`}
            title="Open in chat bar"
          >
            <ExternalLink className="w-5 h-5" />
          </button>
          
          {/* Group settings button for group chats */}
          {!chatData.isPrivate && (
            <button
              onClick={() => setShowGroupSettings(true)}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
              title="Group settings"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
          
          {/* Close button */}
          <button
            onClick={handleCloseChat}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700 border border-gray-200"
            title="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-gray-50">
        {messages.map((msg, index) => {
          const isFirstInGroup = index === 0 || messages[index - 1].sender !== msg.sender;
          const isLastInGroup = index === messages.length - 1 || messages[index + 1].sender !== msg.sender;
          
          return (
            <MessageBubble
              key={msg.id}
              message={msg}
              isOwn={msg.sender === currentUser?.email}
              showSender={isFirstInGroup}
              showTimestamp={isLastInGroup}
              senderDisplayName={getDisplayName(msg.sender)}
              senderPhotoURL={getPhotoURL(msg.sender)}
              chatId={chatId}
              currentUserEmail={currentUser?.email}
              onEditMessage={editMessage}
              onReplyMessage={handleReplyToMessage}
              getDisplayName={getDisplayName}
            />
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Typing Indicator - Master Admin Only */}
      {isMasterAdmin && typingUsers.length > 0 && (
        <TypingIndicator 
          typingUsers={typingUsers} 
          className="border-t border-gray-200 bg-gray-50" 
        />
      )}

      {/* Input */}
      {!isReadOnly ? (
        <div className="border-t border-gray-200 bg-white">
          {/* Reply Preview */}
          {replyToMessage && (
            <div className="px-4 pt-3 pb-0">
              <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border-l-4 border-teal-500">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-teal-700 mb-0.5">
                    Replying to {getDisplayName(replyToMessage.sender)}
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {replyToMessage.text || (replyToMessage.imageUrl ? '📷 Image' : '📎 Attachment')}
                  </p>
                </div>
                <button
                  onClick={cancelReply}
                  className="p-1 hover:bg-teal-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                  title="Cancel reply"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          <div className="p-4">
            <MessageInput 
              onSend={sendMessage} 
              chatId={chatId}
              currentUser={currentUser}
            />
          </div>
        </div>
      ) : (
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-amber-700 text-sm">
            <span className="font-medium">Read-only mode:</span> You are viewing this chat as an admin. No messages can be sent.
          </div>
        </div>
      )}

      {/* Group Settings Modal - also shown for private chats when adding users */}
      {showGroupSettings && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-[500px] max-h-[80vh] overflow-y-auto border border-gray-200 shadow-xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {chatData.isPrivate ? 'Chat Settings' : 'Group Chat Settings'}
              </h3>
              <button
                onClick={() => {
                  setShowGroupSettings(false);
                  setShowAddUser(false);
                  setAddUserSearch('');
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Name Section - Only for group chats */}
            {!chatData.isPrivate && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Chat Name</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newChatName || chatData.name || ''}
                    onChange={(e) => setNewChatName(e.target.value)}
                    placeholder="Enter chat name..."
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  />
                  <button
                    onClick={handleRenameChat}
                    disabled={!newChatName?.trim() || newChatName === chatData.name}
                    className="px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            )}

            {/* Members Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-900">
                  Members ({chatData.participants?.length || 0})
                </h4>
                <button
                  onClick={() => {
                    setShowAddUser(!showAddUser);
                    if (!showAddUser) loadAllUsers();
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-teal-500 hover:bg-teal-600 rounded-lg text-white text-sm font-medium transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Add User
                </button>
              </div>
              
              {/* Add User Section */}
              {showAddUser && (
                <div className="mb-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={addUserSearch}
                      onChange={(e) => filterUsers(e.target.value)}
                      placeholder="Search users by name or email..."
                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  
                  {isLoadingUsers ? (
                    <div className="text-center py-4 text-gray-500">Loading users...</div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {filteredUsers
                        .filter((user: any) => !chatData.participants?.includes(user.email?.toLowerCase()))
                        .slice(0, 20)
                        .map((user: any) => (
                          <div
                            key={user.id}
                            onClick={() => addUserToExistingChat(user)}
                            className="flex items-center gap-3 p-2 hover:bg-teal-100 rounded-lg cursor-pointer transition-colors"
                          >
                            <ProfileImage
                              src={user.photoURL}
                              alt={user.displayName || user.email}
                              className="w-8 h-8 rounded-full"
                              neutral
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 font-medium text-sm truncate">
                                {user.displayName || user.email}
                              </p>
                              <p className="text-gray-500 text-xs truncate">{user.email}</p>
                            </div>
                            {isAddingUser ? (
                              <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4 text-teal-600" />
                            )}
                          </div>
                        ))}
                      {filteredUsers.filter((user: any) => !chatData.participants?.includes(user.email?.toLowerCase())).length === 0 && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          {addUserSearch ? 'No users found matching your search' : 'All users are already in this chat'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {chatData.participants?.map((email: string) => (
                  <div key={email} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <ProfileImage
                      src={getPhotoURL(email)}
                      alt={getDisplayName(email)}
                      className="w-10 h-10 rounded-full"
                      neutral
                    />
                    <div className="flex-1">
                      <p className="text-gray-900 font-medium">{getDisplayName(email)}</p>
                      <p className="text-gray-500 text-sm">{email}</p>
                    </div>
                    {email === currentUser?.email && (
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                        You
                      </span>
                    )}
                    {email === chatData.createdBy && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                        Creator
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowGroupSettings(false);
                  setShowAddUser(false);
                  setAddUserSearch('');
                }}
                className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatThread;
